/**
 * Service CMS - Gestion des modules JSON
 *
 * Utilise l'API backend NestJS pour la persistance (GET, PUT, DELETE, POST import)
 * Maintient un cache local pour les performances
 */

import type {
  ModuleDefinition,
  ModuleDefinitionV2,
  ModuleWithStats,
  ModuleRevisionStats,
  RevisionStatus,
  ModuleTab,
  ModuleGroup,
  ModuleField,
  FieldGenerationRule,
  GenerationConfig,
} from '../types/cms';
import { migrateToV2Format, generateId, getGroupsByTab } from '@/lib/cms-migration';

// API Configuration
const API_BASE = import.meta.env.VITE_API_URL || '/api/v1';
const CMS_API = `${API_BASE}/cms/modules`;

// Cloudflare Access Service Token (for dev tunnel)
const CF_ACCESS_CLIENT_ID = import.meta.env.VITE_CF_ACCESS_CLIENT_ID || '';
const CF_ACCESS_CLIENT_SECRET = import.meta.env.VITE_CF_ACCESS_CLIENT_SECRET || '';

// Track API availability
let apiAvailable = true;

/**
 * Helper to make API requests with error handling
 */
async function cmsApiRequest<T>(
  url: string,
  options: RequestInit = {}
): Promise<{ data: T | null; error: string | null }> {
  // Skip API call if we already know API is unavailable (fast path for dev mode)
  if (!apiAvailable) {
    return { data: null, error: 'API unavailable (cached)' };
  }

  try {
    // Build headers with optional Cloudflare Access token
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    // Add Cloudflare Access headers if configured (for dev tunnel)
    if (CF_ACCESS_CLIENT_ID && CF_ACCESS_CLIENT_SECRET) {
      headers['CF-Access-Client-Id'] = CF_ACCESS_CLIENT_ID;
      headers['CF-Access-Client-Secret'] = CF_ACCESS_CLIENT_SECRET;
    }

    // Add timeout to prevent hanging when backend is unavailable
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 3000); // 3s timeout

    const response = await fetch(url, {
      ...options,
      headers: {
        ...headers,
        ...options.headers,
      },
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Erreur serveur' }));
      return { data: null, error: error.message || `Erreur HTTP ${response.status}` };
    }

    // Handle 204 No Content
    if (response.status === 204) {
      return { data: null, error: null };
    }

    const data = await response.json();
    apiAvailable = true;
    return { data, error: null };
  } catch (err) {
    apiAvailable = false;
    const message = err instanceof Error ? err.message : 'Erreur de connexion';
    console.error(`CMS API Error (${url}):`, message);
    return { data: null, error: message };
  }
}

/**
 * Check if API is available
 */
export function isApiAvailable(): boolean {
  return apiAvailable;
}

// Cache des modules chargés (exported for synchronous access in LinearNoteGenerator)
export const modulesCache: Map<string, ModuleDefinition> = new Map();

/**
 * Get a module from cache synchronously (returns null if not loaded)
 * Use this when modules have been pre-loaded and you need sync access
 */
export function getModuleFromCache(moduleId: string): ModuleDefinition | null {
  return modulesCache.get(moduleId) ?? null;
}

// ============================================================================
// DERIVED MAPPING UTILITIES (Auto Module Registration)
// ============================================================================

/**
 * Get form field prefix from CMS module (derived from cache)
 * Returns the formFieldPrefix if defined, otherwise derives from moduleId
 * @param moduleId - CMS module ID (e.g., 'signes-vitaux')
 * @returns Form field prefix (e.g., 'signesVitaux') or null if module not cached
 */
export function getFormFieldPrefix(moduleId: string): string | null {
  const module = modulesCache.get(moduleId);
  if (!module) return null;

  // Use explicit formFieldPrefix if defined
  if (module.formFieldPrefix) {
    return module.formFieldPrefix;
  }

  // Fallback: derive from moduleId (convert kebab-case to camelCase)
  return moduleId.replace(/-([a-z])/g, (_, char) => char.toUpperCase());
}

/**
 * Build MODULE_MAPPING from loaded CMS modules
 * Call after all modules are loaded (e.g., after useCmsModulesPreload)
 * @returns Record mapping CMS moduleId → formFieldPrefix
 */
export function buildModuleMapping(): Record<string, string> {
  const mapping: Record<string, string> = {};
  for (const [moduleId, module] of modulesCache.entries()) {
    const prefix =
      module.formFieldPrefix || moduleId.replace(/-([a-z])/g, (_, char) => char.toUpperCase());
    mapping[moduleId] = prefix;
  }
  return mapping;
}

// Liste des modules disponibles
const MODULE_IDS = [
  'signes-vitaux',
  'douleur',
  'etat-general',
  'mobilite',
  'nutrition',
  'elimination-urinaire',
  'elimination-intestinale',
  'cognitif-psychologique',
  'cardiovasculaire',
  'cardio-respiratoire',
  'fonctions-base',
  'evaluation-plaie',
  'soins-plaie-simple',
  'soins-palliatifs',
  'sonde-urinaire',
  'nephrostomie',
  'poc',
  'stomie',
  'catheterisme-vesical',
];

/**
 * Calcule les statistiques de révision pour un module
 */
function calculateRevisionStats(module: ModuleDefinition): ModuleRevisionStats {
  const total = module.fields.length;
  const draft = module.fields.filter((f) => f.revision === 'draft').length;
  const reviewed = module.fields.filter((f) => f.revision === 'reviewed').length;
  const validated = module.fields.filter((f) => f.revision === 'validated').length;

  return {
    total,
    draft,
    reviewed,
    validated,
    percentage: total > 0 ? Math.round((validated / total) * 100) : 0,
  };
}

/**
 * Charge un module JSON depuis l'API (avec fallback vers cache et import dynamique)
 */
export async function loadModule(moduleId: string): Promise<ModuleDefinition | null> {
  // Vérifier le cache d'abord
  if (modulesCache.has(moduleId)) {
    return modulesCache.get(moduleId)!;
  }

  // Tenter de charger depuis l'API
  const { data, error } = await cmsApiRequest<ModuleDefinition>(`${CMS_API}/${moduleId}`);

  if (data) {
    modulesCache.set(moduleId, data);
    return data;
  }

  // Fallback: import dynamique (pour dev sans backend ou offline)
  if (error) {
    console.warn(`API indisponible pour ${moduleId}, fallback vers import local`);
    try {
      const moduleData = await import(`../../data/modules/${moduleId}.json`);
      const module = moduleData.default as ModuleDefinition;
      modulesCache.set(moduleId, module);
      return module;
    } catch (importError) {
      console.error(`Erreur chargement module ${moduleId}:`, importError);
      return null;
    }
  }

  return null;
}

// Type for API list response
interface ModuleListItem {
  moduleId: string;
  displayName: string;
  status: string;
  category: string;
  fieldsCount: number;
  lastModified: string;
}

/**
 * Charge tous les modules avec leurs statistiques depuis l'API
 */
export async function loadAllModules(): Promise<ModuleWithStats[]> {
  // Tenter de charger la liste depuis l'API
  const { data: moduleList, error } = await cmsApiRequest<ModuleListItem[]>(CMS_API);

  if (moduleList && Array.isArray(moduleList)) {
    // Charger chaque module complet pour avoir les stats
    const modules: ModuleWithStats[] = [];
    for (const item of moduleList) {
      const module = await loadModule(item.moduleId);
      if (module) {
        modules.push({
          ...module,
          revisionStats: calculateRevisionStats(module),
        });
      }
    }
    return modules;
  }

  // Fallback: utiliser la liste statique MODULE_IDS
  if (error) {
    console.warn('API indisponible, fallback vers liste statique de modules');
  }

  const modules: ModuleWithStats[] = [];
  for (const moduleId of MODULE_IDS) {
    const module = await loadModule(moduleId);
    if (module) {
      modules.push({
        ...module,
        revisionStats: calculateRevisionStats(module),
      });
    }
  }

  return modules;
}

// Custom error class for save failures
export class SaveModuleError extends Error {
  constructor(
    message: string,
    public moduleId: string
  ) {
    super(message);
    this.name = 'SaveModuleError';
  }
}

/**
 * Sauvegarde un module via l'API backend
 * @throws SaveModuleError si la sauvegarde échoue
 */
export async function saveModule(module: ModuleDefinition): Promise<boolean> {
  // Mettre à jour les métadonnées
  const updatedModule: ModuleDefinition = {
    ...module,
    metadata: {
      ...module.metadata,
      lastModified: new Date().toISOString(),
    },
  };

  // Sauvegarder via l'API
  const { error } = await cmsApiRequest<ModuleDefinition>(`${CMS_API}/${module.moduleId}`, {
    method: 'PUT',
    body: JSON.stringify(updatedModule, null, 2),
  });

  if (error) {
    // Mettre en cache quand même pour ne pas perdre les données
    modulesCache.set(module.moduleId, updatedModule);
    throw new SaveModuleError(error, module.moduleId);
  }

  // Mettre à jour le cache après succès
  modulesCache.set(module.moduleId, updatedModule);
  return true;
}

/**
 * Met à jour le statut de révision d'un champ
 */
export async function updateFieldRevision(
  moduleId: string,
  fieldId: string,
  status: RevisionStatus
): Promise<boolean> {
  const module = await loadModule(moduleId);
  if (!module) return false;

  const fieldIndex = module.fields.findIndex((f) => f.id === fieldId);
  if (fieldIndex === -1) return false;

  const updatedFields = [...module.fields];
  updatedFields[fieldIndex] = {
    ...updatedFields[fieldIndex],
    revision: status,
  };

  // Mettre à jour le statut global du module
  const allValidated = updatedFields.every((f) => f.revision === 'validated');
  const anyReviewed = updatedFields.some(
    (f) => f.revision === 'reviewed' || f.revision === 'validated'
  );

  const updatedModule: ModuleDefinition = {
    ...module,
    fields: updatedFields,
    status: allValidated ? 'validated' : anyReviewed ? 'reviewed' : 'draft',
  };

  return saveModule(updatedModule);
}

/**
 * Met à jour un champ complet
 */
export async function updateField(
  moduleId: string,
  fieldId: string,
  updates: Partial<ModuleDefinition['fields'][0]>
): Promise<boolean> {
  const module = await loadModule(moduleId);
  if (!module) return false;

  const fieldIndex = module.fields.findIndex((f) => f.id === fieldId);
  if (fieldIndex === -1) return false;

  const updatedFields = [...module.fields];
  updatedFields[fieldIndex] = {
    ...updatedFields[fieldIndex],
    ...updates,
  };

  const updatedModule: ModuleDefinition = {
    ...module,
    fields: updatedFields,
  };

  return saveModule(updatedModule);
}

/**
 * Marque tous les champs d'un module comme révisés/validés
 */
export async function bulkUpdateRevision(
  moduleId: string,
  status: RevisionStatus
): Promise<boolean> {
  const module = await loadModule(moduleId);
  if (!module) return false;

  const updatedModule: ModuleDefinition = {
    ...module,
    status,
    fields: module.fields.map((f) => ({ ...f, revision: status })),
  };

  return saveModule(updatedModule);
}

/**
 * Invalide le cache pour forcer un rechargement
 */
export function invalidateCache(moduleId?: string) {
  if (moduleId) {
    modulesCache.delete(moduleId);
  } else {
    modulesCache.clear();
  }
}

/**
 * Exporte un module en JSON (pour téléchargement)
 */
export function exportModuleAsJson(module: ModuleDefinition): string {
  return JSON.stringify(module, null, 2);
}

/**
 * Déclenche le téléchargement d'un module en fichier JSON
 */
export function downloadModuleJson(module: ModuleDefinition): void {
  const json = exportModuleAsJson(module);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = `${module.moduleId}.json`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
}

/**
 * Obtient la liste des IDs de modules
 */
export function getModuleIds(): string[] {
  return [...MODULE_IDS];
}

/**
 * Crée un nouveau module et l'ajoute au cache
 */
export async function createModule(module: ModuleDefinitionV2): Promise<boolean> {
  try {
    // Ajouter au cache
    modulesCache.set(module.moduleId, module);

    // Sauvegarder via API en dev
    return await saveModule(module);
  } catch (error) {
    console.error(`Erreur création module ${module.moduleId}:`, error);
    return false;
  }
}

/**
 * Génère un nouveau moduleId unique basé sur l'original
 */
function generateCopyId(originalId: string, existingIds: string[]): string {
  const baseId = originalId.replace(/-copy(-\d+)?$/, '');
  let copyId = `${baseId}-copy`;
  let counter = 2;

  while (existingIds.includes(copyId)) {
    copyId = `${baseId}-copy-${counter}`;
    counter++;
  }

  return copyId;
}

/**
 * Duplique un module existant
 */
export async function duplicateModule(moduleId: string): Promise<ModuleDefinition | null> {
  const module = await loadModule(moduleId);
  if (!module) return null;

  try {
    // Obtenir tous les IDs existants dans le cache
    const existingIds = Array.from(modulesCache.keys());

    // Générer un nouvel ID
    const newModuleId = generateCopyId(moduleId, existingIds);

    // Créer la copie
    const duplicate: ModuleDefinition = {
      ...module,
      moduleId: newModuleId,
      displayName: `${module.displayName} (copie)`,
      status: 'draft',
      metadata: {
        ...module.metadata,
        author: 'CMS',
        lastModified: new Date().toISOString(),
        notes: `Copie de ${module.moduleId}`,
      },
    };

    // Sauvegarder
    const success = await saveModule(duplicate);
    if (success) {
      return duplicate;
    }
    return null;
  } catch (error) {
    console.error(`Erreur duplication module ${moduleId}:`, error);
    return null;
  }
}

// Custom error class for delete failures
export class DeleteModuleError extends Error {
  constructor(
    message: string,
    public moduleId: string
  ) {
    super(message);
    this.name = 'DeleteModuleError';
  }
}

/**
 * Supprime un module via l'API backend
 * @throws DeleteModuleError si la suppression échoue
 */
export async function deleteModule(moduleId: string): Promise<boolean> {
  // Supprimer via l'API
  const { error } = await cmsApiRequest<void>(`${CMS_API}/${moduleId}`, {
    method: 'DELETE',
  });

  if (error) {
    throw new DeleteModuleError(error, moduleId);
  }

  // Supprimer du cache après succès
  modulesCache.delete(moduleId);
  return true;
}

// ============================================================================
// IMPORT/EXPORT FUNCTIONS
// ============================================================================

export interface ImportResult {
  success: boolean;
  module?: ModuleDefinition;
  error?: string;
}

// API import response type
interface ImportApiResponse {
  moduleId: string;
  displayName: string;
  message: string;
}

/**
 * Importe un module via l'API backend
 * L'API gère la validation, les duplicatas, et la sauvegarde
 */
export async function importModule(jsonContent: string): Promise<ImportResult> {
  try {
    // 1. Validation locale basique du JSON
    let data: unknown;
    try {
      data = JSON.parse(jsonContent);
    } catch {
      return { success: false, error: 'JSON invalide: erreur de syntaxe' };
    }

    if (typeof data !== 'object' || data === null) {
      return { success: false, error: 'JSON invalide: doit être un objet' };
    }

    const moduleData = data as Record<string, unknown>;

    if (!moduleData.moduleId || typeof moduleData.moduleId !== 'string') {
      return { success: false, error: 'Champ requis manquant: moduleId' };
    }

    // 2. Envoyer à l'API pour import
    const { data: response, error } = await cmsApiRequest<ImportApiResponse>(`${CMS_API}/import`, {
      method: 'POST',
      body: jsonContent,
    });

    if (error) {
      return { success: false, error };
    }

    if (!response) {
      return { success: false, error: "Réponse vide de l'API" };
    }

    // 3. Charger le module importé (peut avoir un ID différent si renommé)
    const importedModule = await loadModule(response.moduleId);

    if (importedModule) {
      // Invalider et recharger le cache pour ce module
      modulesCache.set(response.moduleId, importedModule);
      return { success: true, module: importedModule };
    }

    return { success: true, module: undefined };
  } catch (error) {
    console.error('Erreur import module:', error);
    const message = error instanceof Error ? error.message : "Erreur inattendue lors de l'import";
    return { success: false, error: message };
  }
}

// ============================================================================
// V2 FORMAT FUNCTIONS
// ============================================================================

/**
 * Charge un module et le migre au format V2 si nécessaire
 */
export async function loadModuleV2(moduleId: string): Promise<ModuleDefinitionV2 | null> {
  const module = await loadModule(moduleId);
  if (!module) return null;

  return migrateToV2Format(module);
}

/** Type pour module V2 avec statistiques */
export type ModuleWithStatsV2 = ModuleDefinitionV2 & { revisionStats: ModuleRevisionStats };

/**
 * Charge tous les modules au format V2 avec leurs statistiques
 */
export async function loadAllModulesV2(): Promise<ModuleWithStatsV2[]> {
  const modules = await loadAllModules();
  return modules.map((module) => ({
    ...migrateToV2Format(module),
    revisionStats: module.revisionStats,
  }));
}

// ============================================================================
// TAB CRUD FUNCTIONS
// ============================================================================

/**
 * Ajoute un nouvel onglet a un module
 * @param moduleId - ID du module
 * @param label - Label du nouvel onglet
 * @returns Le nouvel onglet cree ou null si erreur
 */
export async function addTab(moduleId: string, label: string): Promise<ModuleTab | null> {
  const module = await loadModuleV2(moduleId);
  if (!module) {
    console.error(`Module ${moduleId} non trouve`);
    return null;
  }

  try {
    // Generer un ID unique
    const existingTabIds = module.tabs.map((t) => t.id);
    const newId = generateId(label, existingTabIds);

    // Calculer l'ordre (max + 1)
    const maxOrder = module.tabs.reduce((max, t) => Math.max(max, t.order), 0);

    // Creer le nouvel onglet
    const newTab: ModuleTab = {
      id: newId,
      label,
      order: maxOrder + 1,
    };

    // Mettre a jour le module
    const updatedModule: ModuleDefinitionV2 = {
      ...module,
      tabs: [...module.tabs, newTab],
    };

    // Sauvegarder
    const success = await saveModule(updatedModule);
    if (success) {
      return newTab;
    }
    return null;
  } catch (error) {
    console.error(`Erreur ajout onglet au module ${moduleId}:`, error);
    return null;
  }
}

/**
 * Renomme un onglet existant
 * @param moduleId - ID du module
 * @param tabId - ID de l'onglet a renommer
 * @param newLabel - Nouveau label
 * @returns true si succes, false sinon
 */
export async function renameTab(
  moduleId: string,
  tabId: string,
  newLabel: string
): Promise<boolean> {
  const module = await loadModuleV2(moduleId);
  if (!module) {
    console.error(`Module ${moduleId} non trouve`);
    return false;
  }

  try {
    // Trouver l'onglet
    const tabIndex = module.tabs.findIndex((t) => t.id === tabId);
    if (tabIndex === -1) {
      console.error(`Onglet ${tabId} non trouve dans le module ${moduleId}`);
      return false;
    }

    // Mettre a jour le label (garder le meme ID pour les references)
    const updatedTabs = [...module.tabs];
    updatedTabs[tabIndex] = {
      ...updatedTabs[tabIndex],
      label: newLabel,
    };

    // Sauvegarder
    const updatedModule: ModuleDefinitionV2 = {
      ...module,
      tabs: updatedTabs,
    };

    return await saveModule(updatedModule);
  } catch (error) {
    console.error(`Erreur renommage onglet ${tabId}:`, error);
    return false;
  }
}

/**
 * Supprime un onglet et deplace ses groupes vers le premier onglet restant
 * @param moduleId - ID du module
 * @param tabId - ID de l'onglet a supprimer
 * @returns true si succes, false sinon
 */
export async function deleteTab(moduleId: string, tabId: string): Promise<boolean> {
  const module = await loadModuleV2(moduleId);
  if (!module) {
    console.error(`Module ${moduleId} non trouve`);
    return false;
  }

  try {
    // Verifier qu'il reste plus d'un onglet
    if (module.tabs.length <= 1) {
      console.warn(`Impossible de supprimer le dernier onglet du module ${moduleId}`);
      return false;
    }

    // Trouver l'onglet a supprimer
    const tabIndex = module.tabs.findIndex((t) => t.id === tabId);
    if (tabIndex === -1) {
      console.error(`Onglet ${tabId} non trouve dans le module ${moduleId}`);
      return false;
    }

    // Supprimer l'onglet
    const updatedTabs = module.tabs.filter((t) => t.id !== tabId);

    // Trouver le premier onglet restant (par ordre)
    const sortedTabs = [...updatedTabs].sort((a, b) => a.order - b.order);
    const firstTabId = sortedTabs[0].id;

    // Deplacer les groupes orphelins vers le premier onglet
    const updatedGroups = module.groups.map((group) => {
      if (group.tabId === tabId) {
        return { ...group, tabId: firstTabId };
      }
      return group;
    });

    // Sauvegarder
    const updatedModule: ModuleDefinitionV2 = {
      ...module,
      tabs: updatedTabs,
      groups: updatedGroups,
    };

    return await saveModule(updatedModule);
  } catch (error) {
    console.error(`Erreur suppression onglet ${tabId}:`, error);
    return false;
  }
}

// ============================================================================
// GROUP CRUD FUNCTIONS
// ============================================================================

/**
 * Ajoute un nouveau groupe a un onglet
 * @param moduleId - ID du module
 * @param tabId - ID de l'onglet parent
 * @param label - Label du nouveau groupe
 * @returns Le nouveau groupe cree ou null si erreur
 */
export async function addGroup(
  moduleId: string,
  tabId: string,
  label: string
): Promise<ModuleGroup | null> {
  const module = await loadModuleV2(moduleId);
  if (!module) {
    console.error(`Module ${moduleId} non trouve`);
    return null;
  }

  try {
    // Generer un ID unique
    const existingGroupIds = module.groups.map((g) => g.id);
    const newId = generateId(label, existingGroupIds);

    // Calculer l'ordre (max + 1 parmi les groupes du meme onglet)
    const tabGroups = getGroupsByTab(module, tabId);
    const maxOrder = tabGroups.reduce((max, g) => Math.max(max, g.order), 0);

    // Creer le nouveau groupe
    const newGroup: ModuleGroup = {
      id: newId,
      tabId,
      label,
      order: maxOrder + 1,
    };

    // Mettre a jour le module
    const updatedModule: ModuleDefinitionV2 = {
      ...module,
      groups: [...module.groups, newGroup],
    };

    // Sauvegarder
    const success = await saveModule(updatedModule);
    if (success) {
      return newGroup;
    }
    return null;
  } catch (error) {
    console.error(`Erreur ajout groupe au module ${moduleId}:`, error);
    return null;
  }
}

/**
 * Renomme un groupe existant
 * @param moduleId - ID du module
 * @param groupId - ID du groupe a renommer
 * @param newLabel - Nouveau label
 * @returns true si succes, false sinon
 */
export async function renameGroup(
  moduleId: string,
  groupId: string,
  newLabel: string
): Promise<boolean> {
  const module = await loadModuleV2(moduleId);
  if (!module) {
    console.error(`Module ${moduleId} non trouve`);
    return false;
  }

  try {
    // Trouver le groupe
    const groupIndex = module.groups.findIndex((g) => g.id === groupId);
    if (groupIndex === -1) {
      console.error(`Groupe ${groupId} non trouve dans le module ${moduleId}`);
      return false;
    }

    // Mettre a jour le label (garder le meme ID pour les references)
    const updatedGroups = [...module.groups];
    updatedGroups[groupIndex] = {
      ...updatedGroups[groupIndex],
      label: newLabel,
    };

    // Sauvegarder
    const updatedModule: ModuleDefinitionV2 = {
      ...module,
      groups: updatedGroups,
    };

    return await saveModule(updatedModule);
  } catch (error) {
    console.error(`Erreur renommage groupe ${groupId}:`, error);
    return false;
  }
}

/**
 * Supprime un groupe et deplace ses champs vers le premier groupe restant de l'onglet
 * @param moduleId - ID du module
 * @param groupId - ID du groupe a supprimer
 * @returns true si succes, false sinon
 */
export async function deleteGroup(moduleId: string, groupId: string): Promise<boolean> {
  const module = await loadModuleV2(moduleId);
  if (!module) {
    console.error(`Module ${moduleId} non trouve`);
    return false;
  }

  try {
    // Trouver le groupe a supprimer
    const groupToDelete = module.groups.find((g) => g.id === groupId);
    if (!groupToDelete) {
      console.error(`Groupe ${groupId} non trouve dans le module ${moduleId}`);
      return false;
    }

    // Verifier qu'il reste plus d'un groupe dans l'onglet parent
    const tabGroups = getGroupsByTab(module, groupToDelete.tabId);
    if (tabGroups.length <= 1) {
      console.warn(`Impossible de supprimer le dernier groupe de l'onglet ${groupToDelete.tabId}`);
      return false;
    }

    // Supprimer le groupe
    const updatedGroups = module.groups.filter((g) => g.id !== groupId);

    // Trouver le premier groupe restant dans l'onglet (par ordre)
    const remainingTabGroups = updatedGroups
      .filter((g) => g.tabId === groupToDelete.tabId)
      .sort((a, b) => a.order - b.order);
    const firstGroupId = remainingTabGroups[0].id;

    // Deplacer les champs orphelins vers le premier groupe
    const updatedFields = module.fields.map((field) => {
      if (field.groupId === groupId) {
        return { ...field, groupId: firstGroupId };
      }
      return field;
    });

    // Sauvegarder
    const updatedModule: ModuleDefinitionV2 = {
      ...module,
      groups: updatedGroups,
      fields: updatedFields,
    };

    return await saveModule(updatedModule);
  } catch (error) {
    console.error(`Erreur suppression groupe ${groupId}:`, error);
    return false;
  }
}

// ============================================================================
// FIELD CRUD FUNCTIONS
// ============================================================================

/**
 * Ajoute un nouveau champ a un groupe
 * @param moduleId - ID du module
 * @param groupId - ID du groupe parent
 * @param field - Donnees du champ (sans id)
 * @returns Le nouveau champ cree ou null si erreur
 */
export async function addField(
  moduleId: string,
  groupId: string,
  field: Omit<ModuleField, 'id'>
): Promise<ModuleField | null> {
  const module = await loadModuleV2(moduleId);
  if (!module) {
    console.error(`Module ${moduleId} non trouve`);
    return null;
  }

  try {
    // Generer un ID unique
    const newId = `field-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    // Calculer l'ordre (max + 1 parmi les champs du meme groupe)
    const groupFields = module.fields.filter((f) => f.groupId === groupId);
    const maxOrder = groupFields.reduce((max, f) => Math.max(max, f.order || 0), 0);

    // Creer le nouveau champ
    const newField: ModuleField = {
      ...field,
      id: newId,
      groupId,
      section: '', // Legacy compat
      order: maxOrder + 1,
    };

    // Mettre a jour le module
    const updatedModule: ModuleDefinitionV2 = {
      ...module,
      fields: [...module.fields, newField],
    };

    // Sauvegarder
    const success = await saveModule(updatedModule);
    if (success) {
      return newField;
    }
    return null;
  } catch (error) {
    console.error(`Erreur ajout champ au module ${moduleId}:`, error);
    return null;
  }
}

/**
 * Met a jour un champ existant
 * @param moduleId - ID du module
 * @param fieldId - ID du champ a mettre a jour
 * @param updates - Champs a mettre a jour
 * @returns true si succes, false sinon
 */
export async function updateFieldData(
  moduleId: string,
  fieldId: string,
  updates: Partial<ModuleField>
): Promise<boolean> {
  const module = await loadModuleV2(moduleId);
  if (!module) {
    console.error(`Module ${moduleId} non trouve`);
    return false;
  }

  try {
    // Trouver le champ
    const fieldIndex = module.fields.findIndex((f) => f.id === fieldId);
    if (fieldIndex === -1) {
      console.error(`Champ ${fieldId} non trouve dans le module ${moduleId}`);
      return false;
    }

    // Mettre a jour le champ
    const updatedFields = [...module.fields];
    updatedFields[fieldIndex] = {
      ...updatedFields[fieldIndex],
      ...updates,
    };

    // Sauvegarder
    const updatedModule: ModuleDefinitionV2 = {
      ...module,
      fields: updatedFields,
    };

    return await saveModule(updatedModule);
  } catch (error) {
    console.error(`Erreur mise a jour champ ${fieldId}:`, error);
    return false;
  }
}

/**
 * Supprime un champ
 * @param moduleId - ID du module
 * @param fieldId - ID du champ a supprimer
 * @returns true si succes, false sinon
 */
export async function deleteField(moduleId: string, fieldId: string): Promise<boolean> {
  const module = await loadModuleV2(moduleId);
  if (!module) {
    console.error(`Module ${moduleId} non trouve`);
    return false;
  }

  try {
    // Supprimer le champ
    const updatedFields = module.fields.filter((f) => f.id !== fieldId);

    // Sauvegarder
    const updatedModule: ModuleDefinitionV2 = {
      ...module,
      fields: updatedFields,
    };

    return await saveModule(updatedModule);
  } catch (error) {
    console.error(`Erreur suppression champ ${fieldId}:`, error);
    return false;
  }
}

/**
 * Duplique un champ existant
 * @param moduleId - ID du module
 * @param fieldId - ID du champ a dupliquer
 * @returns Le nouveau champ cree ou null si erreur
 */
export async function duplicateField(
  moduleId: string,
  fieldId: string
): Promise<ModuleField | null> {
  const module = await loadModuleV2(moduleId);
  if (!module) {
    console.error(`Module ${moduleId} non trouve`);
    return null;
  }

  try {
    // Trouver le champ a dupliquer
    const originalField = module.fields.find((f) => f.id === fieldId);
    if (!originalField) {
      console.error(`Champ ${fieldId} non trouve dans module ${moduleId}`);
      return null;
    }

    // Generer un nouvel ID unique
    const existingIds = module.fields.map((f) => f.id);
    const newFieldId = generateCopyId(fieldId, existingIds);

    // Creer la copie du champ
    const duplicatedField: ModuleField = {
      ...originalField,
      id: newFieldId,
      label: `${originalField.label} (copie)`,
      order: originalField.order + 0.5, // Inserer juste apres l'original
    };

    // Trouver l'index du champ original et inserer la copie juste apres
    const originalIndex = module.fields.findIndex((f) => f.id === fieldId);
    const updatedFields = [...module.fields];
    updatedFields.splice(originalIndex + 1, 0, duplicatedField);

    // Renormaliser les ordres
    const normalizedFields = updatedFields.map((f, index) => ({
      ...f,
      order: index,
    }));

    // Dupliquer la fieldRule si elle existe dans generationConfig
    let updatedGenerationConfig = module.generationConfig;
    if (module.generationConfig?.fieldRules) {
      const originalRule = module.generationConfig.fieldRules.find(
        (rule) => rule.fieldId === fieldId
      );
      if (originalRule) {
        const duplicatedRule = {
          ...originalRule,
          fieldId: newFieldId,
        };
        // Trouver l'index de la regle originale et inserer la copie juste apres
        const ruleIndex = module.generationConfig.fieldRules.findIndex(
          (rule) => rule.fieldId === fieldId
        );
        const updatedRules = [...module.generationConfig.fieldRules];
        updatedRules.splice(ruleIndex + 1, 0, duplicatedRule);

        updatedGenerationConfig = {
          ...module.generationConfig,
          fieldRules: updatedRules,
        };
      }
    }

    // Sauvegarder
    const updatedModule: ModuleDefinitionV2 = {
      ...module,
      fields: normalizedFields,
      generationConfig: updatedGenerationConfig,
    };

    const success = await saveModule(updatedModule);
    if (success) {
      return duplicatedField;
    }
    return null;
  } catch (error) {
    console.error(`Erreur duplication champ ${fieldId}:`, error);
    return null;
  }
}

// ============================================================================
// REORDER FUNCTIONS
// ============================================================================

/**
 * Reordonne les onglets d'un module
 * @param moduleId - ID du module
 * @param orderedTabIds - Liste des IDs d'onglets dans le nouvel ordre
 * @returns true si succes, false sinon
 */
export async function reorderTabs(moduleId: string, orderedTabIds: string[]): Promise<boolean> {
  const module = await loadModuleV2(moduleId);
  if (!module) {
    console.error(`Module ${moduleId} non trouve`);
    return false;
  }

  try {
    // Mettre a jour l'ordre de chaque onglet
    const updatedTabs = module.tabs.map((tab) => {
      const newOrder = orderedTabIds.indexOf(tab.id);
      if (newOrder === -1) {
        // Tab non trouve dans la liste, garder son ordre actuel
        return tab;
      }
      return { ...tab, order: newOrder };
    });

    // Sauvegarder
    const updatedModule: ModuleDefinitionV2 = {
      ...module,
      tabs: updatedTabs,
    };

    return await saveModule(updatedModule);
  } catch (error) {
    console.error(`Erreur reordonnancement onglets du module ${moduleId}:`, error);
    return false;
  }
}

/**
 * Reordonne un groupe (deplace vers un nouvel onglet et/ou nouvel ordre)
 * @param moduleId - ID du module
 * @param groupId - ID du groupe a deplacer
 * @param newTabId - ID de l'onglet cible
 * @param newOrder - Nouvelle position dans l'onglet cible
 * @returns true si succes, false sinon
 */
export async function reorderGroups(
  moduleId: string,
  groupId: string,
  newTabId: string,
  newOrder: number
): Promise<boolean> {
  const module = await loadModuleV2(moduleId);
  if (!module) {
    console.error(`Module ${moduleId} non trouve`);
    return false;
  }

  try {
    // Trouver le groupe a deplacer
    const groupToMove = module.groups.find((g) => g.id === groupId);
    if (!groupToMove) {
      console.error(`Groupe ${groupId} non trouve dans le module ${moduleId}`);
      return false;
    }

    const oldTabId = groupToMove.tabId;

    // Obtenir tous les groupes de l'onglet cible (sans le groupe deplace)
    const targetTabGroups = module.groups
      .filter((g) => g.tabId === newTabId && g.id !== groupId)
      .sort((a, b) => a.order - b.order);

    // Inserer le groupe a la nouvelle position
    const reorderedTargetGroups = [
      ...targetTabGroups.slice(0, newOrder),
      { ...groupToMove, tabId: newTabId },
      ...targetTabGroups.slice(newOrder),
    ];

    // Reassigner les ordres dans l'onglet cible
    const updatedTargetGroups = reorderedTargetGroups.map((g, index) => ({
      ...g,
      order: index,
    }));

    // Si deplacement inter-onglets, nettoyer l'onglet source
    let updatedSourceGroups: ModuleGroup[] = [];
    if (oldTabId !== newTabId) {
      const sourceTabGroups = module.groups
        .filter((g) => g.tabId === oldTabId && g.id !== groupId)
        .sort((a, b) => a.order - b.order);

      updatedSourceGroups = sourceTabGroups.map((g, index) => ({
        ...g,
        order: index,
      }));
    }

    // Reconstruire la liste complete des groupes
    const otherGroups = module.groups.filter((g) => g.tabId !== newTabId && g.tabId !== oldTabId);

    const updatedGroups = [
      ...otherGroups,
      ...updatedTargetGroups,
      ...(oldTabId !== newTabId ? updatedSourceGroups : []),
    ];

    // Sauvegarder
    const updatedModule: ModuleDefinitionV2 = {
      ...module,
      groups: updatedGroups,
    };

    return await saveModule(updatedModule);
  } catch (error) {
    console.error(`Erreur reordonnancement groupe ${groupId}:`, error);
    return false;
  }
}

/**
 * Reordonne un champ (deplace vers un nouveau groupe et/ou nouvel ordre)
 * @param moduleId - ID du module
 * @param fieldId - ID du champ a deplacer
 * @param targetGroupId - ID du groupe cible
 * @param newOrder - Nouvelle position dans le groupe cible
 * @returns true si succes, false sinon
 */
export async function reorderFields(
  moduleId: string,
  fieldId: string,
  targetGroupId: string,
  newOrder: number
): Promise<boolean> {
  const module = await loadModuleV2(moduleId);
  if (!module) {
    console.error(`Module ${moduleId} non trouve`);
    return false;
  }

  try {
    // Trouver le champ a deplacer
    const fieldToMove = module.fields.find((f) => f.id === fieldId);
    if (!fieldToMove) {
      console.error(`Champ ${fieldId} non trouve dans le module ${moduleId}`);
      return false;
    }

    const oldGroupId = fieldToMove.groupId;

    // Obtenir tous les champs du groupe cible (sans le champ deplace)
    const targetGroupFields = module.fields
      .filter((f) => f.groupId === targetGroupId && f.id !== fieldId)
      .sort((a, b) => (a.order || 0) - (b.order || 0));

    // Inserer le champ a la nouvelle position
    const reorderedTargetFields = [
      ...targetGroupFields.slice(0, newOrder),
      { ...fieldToMove, groupId: targetGroupId },
      ...targetGroupFields.slice(newOrder),
    ];

    // Reassigner les ordres dans le groupe cible
    const updatedTargetFields = reorderedTargetFields.map((f, index) => ({
      ...f,
      order: index,
    }));

    // Si deplacement inter-groupes, nettoyer le groupe source
    let updatedSourceFields: ModuleField[] = [];
    if (oldGroupId !== targetGroupId) {
      const sourceGroupFields = module.fields
        .filter((f) => f.groupId === oldGroupId && f.id !== fieldId)
        .sort((a, b) => (a.order || 0) - (b.order || 0));

      updatedSourceFields = sourceGroupFields.map((f, index) => ({
        ...f,
        order: index,
      }));
    }

    // Reconstruire la liste complete des champs
    const otherFields = module.fields.filter(
      (f) => f.groupId !== targetGroupId && f.groupId !== oldGroupId
    );

    const updatedFields = [
      ...otherFields,
      ...updatedTargetFields,
      ...(oldGroupId !== targetGroupId ? updatedSourceFields : []),
    ];

    // Sauvegarder
    const updatedModule: ModuleDefinitionV2 = {
      ...module,
      fields: updatedFields,
    };

    return await saveModule(updatedModule);
  } catch (error) {
    console.error(`Erreur reordonnancement champ ${fieldId}:`, error);
    return false;
  }
}

// ============================================================================
// FIELD GENERATION RULE CRUD FUNCTIONS
// ============================================================================

/** Default generationConfig structure when missing */
const DEFAULT_GENERATION_CONFIG: GenerationConfig = {
  version: '1.0',
  fieldRules: [],
  postProcessing: { trimEmpty: true, clinicalFormat: true },
};

/**
 * Update or create a field generation rule
 * @param moduleId - ID du module
 * @param fieldId - ID du champ
 * @param rule - Règle de génération
 * @returns Promise<boolean> - true si succès
 */
export async function updateFieldRule(
  moduleId: string,
  fieldId: string,
  rule: FieldGenerationRule
): Promise<boolean> {
  try {
    // Load module
    const module = await loadModuleV2(moduleId);
    if (!module) {
      console.error(`Module ${moduleId} non trouvé`);
      return false;
    }

    // Ensure generationConfig exists
    const generationConfig: GenerationConfig = module.generationConfig || {
      ...DEFAULT_GENERATION_CONFIG,
    };

    // Ensure fieldRules array exists
    if (!generationConfig.fieldRules) {
      generationConfig.fieldRules = [];
    }

    // Find existing rule index
    const existingIndex = generationConfig.fieldRules.findIndex((r) => r.fieldId === fieldId);

    // Ensure rule has correct fieldId
    const ruleWithFieldId = { ...rule, fieldId };

    if (existingIndex >= 0) {
      // Update existing rule
      generationConfig.fieldRules[existingIndex] = ruleWithFieldId;
    } else {
      // Add new rule
      generationConfig.fieldRules.push(ruleWithFieldId);
    }

    // Save module with updated config
    const updatedModule: ModuleDefinitionV2 = {
      ...module,
      generationConfig,
    };

    return await saveModule(updatedModule);
  } catch (error) {
    console.error(`Erreur mise à jour fieldRule ${fieldId} dans ${moduleId}:`, error);
    return false;
  }
}

/**
 * Reorder field generation rules by updating their order property
 * @param moduleId - ID du module
 * @param orderedFieldIds - Liste des fieldIds dans le nouvel ordre
 * @returns Promise<boolean> - true si succès
 */
export async function reorderFieldRules(
  moduleId: string,
  orderedFieldIds: string[]
): Promise<boolean> {
  try {
    // Load module
    const module = await loadModuleV2(moduleId);
    if (!module) {
      console.error(`Module ${moduleId} non trouvé`);
      return false;
    }

    // Ensure generationConfig and fieldRules exist
    if (!module.generationConfig?.fieldRules) {
      console.warn(`Module ${moduleId} n'a pas de fieldRules à réordonner`);
      return true; // No rules to reorder, consider it success
    }

    // Update order property on each existing rule
    const updatedRules = module.generationConfig.fieldRules.map((rule) => {
      const newOrder = orderedFieldIds.indexOf(rule.fieldId);
      if (newOrder !== -1) {
        // Found in ordered list, assign new order
        return { ...rule, order: newOrder };
      }
      // Not in ordered list, preserve existing order or assign high value
      return rule;
    });

    // Save module with updated rules
    const updatedModule: ModuleDefinitionV2 = {
      ...module,
      generationConfig: {
        ...module.generationConfig,
        fieldRules: updatedRules,
      },
    };

    return await saveModule(updatedModule);
  } catch (error) {
    console.error(`Erreur réordonnancement fieldRules dans ${moduleId}:`, error);
    return false;
  }
}

/**
 * Delete a field generation rule
 * @param moduleId - ID du module
 * @param fieldId - ID du champ
 * @returns Promise<boolean> - true si succès
 */
export async function deleteFieldRule(moduleId: string, fieldId: string): Promise<boolean> {
  try {
    // Load module
    const module = await loadModuleV2(moduleId);
    if (!module) {
      console.error(`Module ${moduleId} non trouvé`);
      return false;
    }

    // Check if generationConfig exists
    if (!module.generationConfig?.fieldRules) {
      // No rules to delete
      return true;
    }

    // Filter out the rule
    const updatedRules = module.generationConfig.fieldRules.filter((r) => r.fieldId !== fieldId);

    // If no change, return success
    if (updatedRules.length === module.generationConfig.fieldRules.length) {
      return true;
    }

    // Save module with updated rules
    const updatedModule: ModuleDefinitionV2 = {
      ...module,
      generationConfig: {
        ...module.generationConfig,
        fieldRules: updatedRules,
      },
    };

    return await saveModule(updatedModule);
  } catch (error) {
    console.error(`Erreur suppression fieldRule ${fieldId} dans ${moduleId}:`, error);
    return false;
  }
}
